import { defineMessages } from 'react-intl';

export const demoFlowMessages = defineMessages({
  captureYourSession: {
    defaultMessage: 'Capture your session',
    id: 'wTXjjPDLiO',
    description: 'Demo entry overlay heading when simplification flag is off',
  },
  createTestSession: {
    defaultMessage: 'Create a test session',
    id: '0/sJcYFo1F',
    description: 'Demo entry overlay heading when simplification flag is on',
  },
  noteWillShowHere: {
    defaultMessage: 'Your note will show here after',
    id: 'Ag01u4ov3v',
    description: 'Demo entry overlay subtitle',
  },
  tryADemo: {
    defaultMessage: 'Try a short demo',
    id: '0Dme/470qN',
    description: 'Primary CTA to start the demo recording flow',
  },
  checkingPermission: {
    defaultMessage: 'Checking...',
    id: 'tKzErM7g67',
    description: 'Demo entry CTA label while microphone permission is being requested',
  },
  micBlockedTitle: {
    defaultMessage: 'Microphone access is blocked',
    id: 'cflMfGp0oz',
    description: 'Title when the browser has system-blocked microphone access',
  },
  micBlockedDescription: {
    defaultMessage:
      'Heidi can’t access your microphone because it’s blocked in your browser settings. Allow microphone access for this site to try the demo.',
    id: 'DeZ05qKQiK',
    description: 'Description when the browser has system-blocked microphone access',
  },
  micBlockedHelpText: {
    defaultMessage:
      'Click the lock or info icon next to your browser’s address bar (or open browser settings) and allow microphone access. We’ll detect the change automatically.',
    id: 'EaRpqh/31F',
    description:
      'Help text guiding the user to update browser settings for microphone access',
  },
  micDeniedTitle: {
    defaultMessage: 'Microphone access needed',
    id: '52p+1ybbMs',
    description: 'Title when microphone permission was denied but can be re-requested',
  },
  micDeniedDescription: {
    defaultMessage:
      'To try the demo, Heidi needs access to your microphone to transcribe your voice.',
    id: 'ou5UQ5ftDj',
    description: 'Description when microphone permission was denied',
  },
  allowMicrophone: {
    defaultMessage: 'Allow microphone',
    id: '3QjlxrAYx9',
    description: 'Button to request microphone permission for the demo',
  },
  skipToResults: {
    defaultMessage: 'Skip to results instead',
    id: '8MYMoSd49o',
    description: 'Link to skip the demo recording and show generated results',
  },
  micDeniedHelpText: {
    defaultMessage:
      'Click ‘Allow microphone’ and select ‘Allow’ when your browser asks for permission.',
    id: 'DVSe7lcFML',
    description: 'Help text for re-requesting microphone permission',
  },
  scriptHeaderTitle: {
    defaultMessage: 'Demo',
    id: 'YpBqwm0qcK',
    description: 'Header label in the demo script modal',
  },
  readScriptTitle: {
    defaultMessage: 'Read the script aloud',
    id: 'Odyme++nVV',
    description: 'Title in the demo script modal and screen-reader label',
  },
  scriptInstructions: {
    defaultMessage: 'Select ‘End transcribing’ below when finished.',
    id: 'Ag6we8hlAn',
    description: 'Instructions in the demo script modal',
  },
  defaultDemoScript: {
    defaultMessage:
      'Doctor: What brings you in today?\nPatient: I think I have the flu.\nDoctor: Tell me more about it.\nPatient: Over the last few days, I’ve had body aches and a runny nose.\nDoctor: Okay, why don’t you sit up on the bed, and I’ll take a look at you.\nPatient: Sure.\nDoctor: It does seem like you have a viral illness. You’ve got a slight fever, but your examination is normal. I’ll send off the swab I took from your nose to check if you have COVID or the flu. We might not find anything specific. In the meantime, I’d suggest rest, keeping hydrated, and using over-the-counter medicines like paracetamol or pseudoephedrine to manage your symptoms.\nPatient: Okay, thanks, doc.',
    id: '1l+QGnRg3e',
    description:
      'Short clinician and patient script read aloud during the first-run demo. Preserve each speaker label and line break.',
  },
  cantTalkShowResults: {
    defaultMessage: 'Can’t talk now? <link>Show the results</link>',
    id: 'nKxufoc+Rw',
    description: 'Prompt with link to skip reading the demo script aloud',
  },
  stopTranscribing: {
    defaultMessage: 'End transcribing',
    id: 'xcvm0k5fsf',
    description: 'Button to stop demo transcription',
  },
  microphoneFallback: {
    defaultMessage: 'Microphone {index}',
    id: 'RPJTdOZAIG',
    description: 'Fallback label for an audio input device without a name',
  },
  noInputDevices: {
    defaultMessage:
      'No input device found. Please check your permissions or input devices and refresh the page',
    id: 'T5nj8s1o2M',
    description: 'Empty state when no microphone devices are available in the demo modal',
  },
  resetDemo: {
    defaultMessage: 'Reset Demo',
    id: 'C2qs4vxDdm',
    description: 'Dev-only button label to reset demo flow state',
  },
  clinicalSuggestionsDemoQuery: {
    defaultMessage:
      'What does the current evidence say about best practice for diagnosing and managing type 2 diabetes in adults?',
    id: 'Gc6QjTgVU2',
    description:
      'Pre-filled Ask Heidi query when the user clicks Try now on the clinical suggestions coach mark',
  },
});

export const demoCoachMarkMessages = defineMessages({
  next: {
    defaultMessage: 'Next',
    id: 'zKAY1AsTLQ',
    description: 'Coach mark footer button to advance to the next step',
  },
  tryNow: {
    defaultMessage: 'Try now',
    id: 'O+mJ7eafDv',
    description: 'Coach mark footer button to try the clinical suggestions feature',
  },
  gotIt: {
    defaultMessage: 'Got it',
    id: 'xsRm62KztY',
    description: 'Coach mark footer button on the final step',
  },
  progress: {
    defaultMessage: '{current}/{total}',
    id: 'FQTOeeAYme',
    description: 'Coach mark step progress indicator',
  },
  refineNoteTitle: {
    defaultMessage: 'Refine your note',
    id: 'kVZON4CSww',
    description: 'Coach mark title for the refine note step',
  },
  refineNoteDescription: {
    defaultMessage: 'Adjust tone, structure, or detail - or edit directly in the text.',
    id: '59fyn3hfX8',
    description: 'Coach mark description for the refine note step',
  },
  createDocsTitle: {
    defaultMessage: 'Create docs in one click',
    id: 'Aw8q3yFARH',
    description: 'Coach mark title for the create documents step',
  },
  createDocsDescription: {
    defaultMessage: 'Generate referral letters, forms, and summaries from this session.',
    id: 'oXOOT8CW/y',
    description: 'Coach mark description for the create documents step',
  },
  clinicalSuggestionsTitle: {
    defaultMessage: 'Evidence when you need it',
    id: '/X43gWqvMg',
    description: 'Coach mark title for the clinical suggestions step',
  },
  clinicalSuggestionsDescription: {
    defaultMessage:
      'Get evidence-based answers and considerations, grounded in this visit.',
    id: '9jeL95FJTa',
    description: 'Coach mark description for the clinical suggestions step',
  },
  controlSourcesTitle: {
    defaultMessage: 'Control your sources',
    id: 'q5aJInzTh6',
    description: 'Coach mark title for the control sources step',
  },
  controlSourcesDescription: {
    defaultMessage:
      'Choose the guidelines and research that matter to your practice. Toggle sources on or off anytime.',
    id: 'ZVIq/Lor2V',
    description: 'Coach mark description for the control sources step',
  },
  newSessionTitle: {
    defaultMessage: 'Start a new session',
    id: 'bKVZHvVnmg',
    description: 'Coach mark title for the new session step',
  },
  newSessionDescription: {
    defaultMessage: 'Each patient visit is a session to transcribe and capture notes.',
    id: 'w3mdfTLw/a',
    description: 'Coach mark description for the new session step',
  },
});

export const meetingBookerDurationMessages = defineMessages({
  fifteenMin: {
    defaultMessage: '15 min',
    id: 'QThm0fKdg3',
    description: 'Meeting duration option label for a 15-minute slot',
  },
  thirtyMin: {
    defaultMessage: '30 min',
    id: 'Qnn5KB/yBX',
    description: 'Meeting duration option label for a 30-minute slot',
  },
});
